// 033 — Odontología (Fase 2 del roadmap de formularios MSP, ver PLAN.md). Antes
// solo tenía 4 campos de texto libre vía el motor genérico de Questionnaire
// (motivo, CPOD/ceo-d tipeados a mano, odontograma con "pieza" en texto
// libre) — acá se agrega el resto del instructivo oficial (antecedentes,
// constantes vitales, examen estomatognático, odontograma visual FDI real,
// indicadores de salud bucal, CPO-ceo calculados, exámenes/diagnóstico/
// tratamiento de la Hoja 2). Se convierte a Patrón B (discreto) por el mismo
// motivo que 038: el motor de Questionnaire no soporta `EncabezadoMspCampos`,
// tablas repetibles ni un diagrama interactivo — `033.json` queda intacto,
// nunca se renderiza (mismo tratamiento que `038.json`).
//
// El odontograma es el único contenido con recursos FHIR reales más allá del
// diagnóstico: un `Observation` (un component por pieza-con-dato) + una
// `Condition` por pieza con hallazgo clínicamente activo — ver
// `odontograma/fhir.ts`. El resto es narrativo (`Par[]`).
import type { JSX } from 'react';
import { useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AreaDictado } from './AreaDictado';
import { CampoDictado } from './CampoDictado';
import type { FormularioDiscretoProps, Par } from './index';
import { codeDeDiagnostico, SelectorDiagnosticoCie10, type DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import { conCodigoCie } from './camposSimplesDatos';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional } from './transaccionFhir';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import { bodySiteCondicion, calcularCeod, calcularCpod, condicionesActivas, construirObservationOdontograma, datosOdontogramaPdf, ESTADO_ODONTOGRAMA_VACIO, OdontogramaVisual, SISTEMA_HALLAZGO_ODONTOGRAMA, type EstadoOdontograma } from './odontograma';
import { CATALOGO_HALLAZGOS_ODONTOGRAMA } from '@logiacare/hce-pdf';
import {
  construirObservationIndicadores,
  FILAS_INDICADORES,
  filasIndicadoresIniciales,
  NINGUNA,
  OPCIONES_CALCULO,
  OPCIONES_GINGIVITIS,
  OPCIONES_PLACA,
  paresEstomatognatico,
  paresIndicadores,
  parAntecedentes,
  parInformeExamenes,
  PIEZA_RAYA,
  textoOpcionIndicador,
  TIPOS_INFORME_EXAMENES,
  totalesIndicadores,
  type FilaIndicadores,
} from './indicadoresSaludBucal033';

const CODIGO = '033';
const TOPE_DIAGNOSTICOS = 6;
const TOPE_SESIONES = 8;

const ANTECEDENTES_PERSONALES = [
  'alergiaAntibiotico',
  'alergiaAnestesia',
  'hemorragias',
  'vihSida',
  'tuberculosis',
  'asma',
  'diabetes',
  'hipertensionArterial',
  'enfermedadCardiaca',
  'otro',
] as const;

const ANTECEDENTES_FAMILIARES = [
  'cardiopatia',
  'hipertensionArterial',
  'enfermedadCardiovascular',
  'endocrinoMetabolico',
  'cancer',
  'tuberculosis',
  'enfermedadMental',
  'enfermedadInfecciosa',
  'malformacion',
  'otro',
] as const;

const ITEMS_ESTOMATOGNATICO = [
  'labios',
  'mejillas',
  'maxilarSuperior',
  'maxilarInferior',
  'lengua',
  'paladar',
  'pisoBoca',
  'carrillos',
  'glandulasSalivales',
  'orofaringe',
  'atm',
  'ganglios',
  'otros',
] as const;

// "En caso de no diagnosticar ninguna patología registrar una raya" (instructivo, bloque I): la raya es la opción `ninguna`.
const OPCIONES_NIVEL = [NINGUNA, 'leve', 'moderada', 'severa'] as const;
const OPCIONES_OCLUSION = [NINGUNA, 'I', 'II', 'III'] as const;
const TIPOS_DIAGNOSTICO = ['presuntivo', 'definitivo'] as const;

/** Terminología estándar HL7 — "Presuntivo" = provisional, "Definitivo" = confirmed (mismo criterio que `CLINICAL_STATUS_ALERGIA_ACTIVA` en extractors.ts: catálogo con equivalente HL7 conocido, no texto libre). */
const VERIFICATION_STATUS_POR_TIPO: Record<(typeof TIPOS_DIAGNOSTICO)[number], { coding: { system: string; code: string; display: string }[] }> = {
  presuntivo: { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/condition-ver-status', code: 'provisional', display: 'Provisional' }] },
  definitivo: { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/condition-ver-status', code: 'confirmed', display: 'Confirmed' }] },
};

interface DiagnosticoFila {
  valor: DiagnosticoCie10Valor;
  tipo: (typeof TIPOS_DIAGNOSTICO)[number];
}
const DIAGNOSTICO_VACIO: DiagnosticoFila = { valor: { texto: '', cie10: null }, tipo: 'presuntivo' };

interface TratamientoFila {
  fecha: string;
  diagnosticosComplicaciones: string;
  procedimientos: string;
  prescripciones: string;
  firma: string;
}
const TRATAMIENTO_VACIO: TratamientoFila = { fecha: '', diagnosticosComplicaciones: '', procedimientos: '', prescripciones: '', firma: '' };

function ChecklistGrid({
  claves,
  prefijoI18n,
  prefijoId,
  valores,
  onChange,
}: {
  claves: readonly string[];
  prefijoI18n: string;
  prefijoId: string;
  valores: Record<string, boolean>;
  onChange: (v: Record<string, boolean>) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3">
      {claves.map((clave) => (
        <div key={clave} className="flex items-center gap-2">
          <Checkbox id={`${prefijoId}-${clave}`} checked={valores[clave] === true} onCheckedChange={(v) => onChange({ ...valores, [clave]: v === true })} />
          <Label htmlFor={`${prefijoId}-${clave}`} className="text-sm font-normal">
            {t(`${prefijoI18n}.${clave}`)}
          </Label>
        </div>
      ))}
    </div>
  );
}

function CampoSelect({
  label,
  valor,
  opciones,
  prefijoOpcionesI18n,
  onChange,
}: {
  label: string;
  valor: string;
  opciones: readonly string[];
  prefijoOpcionesI18n?: string;
  onChange: (v: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1">
      <Label>{label}</Label>
      <Select value={valor || undefined} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {opciones.map((op) => (
            <SelectItem key={op} value={op}>
              {prefijoOpcionesI18n ? t(`${prefijoOpcionesI18n}.${op}`) : op}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function Formulario033({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { guardando, error, guardar } = useGuardado();

  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const titulo = t('formularios.f033.titulo');
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, CODIGO, titulo);

  const [motivoConsulta, setMotivoConsulta] = useState('');
  const [embarazada, setEmbarazada] = useState(false);
  const [enfermedadActual, setEnfermedadActual] = useState('');

  const [antecedentesPersonales, setAntecedentesPersonales] = useState<Record<string, boolean>>({});
  const [antecedentesFamiliares, setAntecedentesFamiliares] = useState<Record<string, boolean>>({});
  const [descripcionPersonales, setDescripcionPersonales] = useState('');
  const [descripcionFamiliares, setDescripcionFamiliares] = useState('');

  const [temperatura, setTemperatura] = useState('');
  const [pulso, setPulso] = useState('');
  const [frecuenciaRespiratoria, setFrecuenciaRespiratoria] = useState('');
  const [presionArterial, setPresionArterial] = useState('');

  const [estomatognatico, setEstomatognatico] = useState<Record<string, string>>({});

  const [odontograma, setOdontograma] = useState<EstadoOdontograma>(ESTADO_ODONTOGRAMA_VACIO);
  const cpod = calcularCpod(odontograma);
  const ceod = calcularCeod(odontograma);

  const [filasIndicadores, setFilasIndicadores] = useState<FilaIndicadores[]>(filasIndicadoresIniciales);
  const totalesInd = totalesIndicadores(filasIndicadores);
  const [nivelPeriodontal, setNivelPeriodontal] = useState('');
  const [tipoOclusion, setTipoOclusion] = useState('');
  const [nivelFluorosis, setNivelFluorosis] = useState('');

  const [pedidoExamenes, setPedidoExamenes] = useState('');
  const [informeExamenes, setInformeExamenes] = useState('');
  const [tiposInforme, setTiposInforme] = useState<Record<string, boolean>>({});

  const [diagnosticos, setDiagnosticos] = useState<DiagnosticoFila[]>([]);
  const [tratamiento, setTratamiento] = useState<TratamientoFila[]>([]);

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const ahora = new Date().toISOString();

      const lote = new LoteTransaccional();
      const observationOdontograma = construirObservationOdontograma(odontograma, subject, encounter);
      if (observationOdontograma) lote.crear(observationOdontograma);
      const observationIndicadores = construirObservationIndicadores(
        {
          filas: filasIndicadores,
          periodontal: nivelPeriodontal,
          oclusion: tipoOclusion,
          fluorosis: nivelFluorosis,
          indices: { cpod, ceod },
          hayOdontograma: observationOdontograma !== undefined,
          tiposInforme: TIPOS_INFORME_EXAMENES.filter((k) => tiposInforme[k]),
        },
        subject,
        encounter,
        ahora,
      );
      if (observationIndicadores) lote.crear(observationIndicadores);

      for (const { pieza, hallazgo, superficies } of condicionesActivas(odontograma)) {
        const etiqueta = CATALOGO_HALLAZGOS_ODONTOGRAMA[hallazgo].etiqueta;
        lote.crear({
          resourceType: 'Condition',
          subject,
          encounter,
          code: { coding: [{ system: SISTEMA_HALLAZGO_ODONTOGRAMA, code: hallazgo, display: etiqueta }], text: etiqueta },
          bodySite: bodySiteCondicion(pieza, superficies),
          recordedDate: ahora,
        });
      }

      for (const diagnostico of diagnosticos) {
        if (diagnostico.valor.texto.trim()) {
          lote.crear({
            resourceType: 'Condition',
            subject,
            encounter,
            code: codeDeDiagnostico(diagnostico.valor),
            verificationStatus: VERIFICATION_STATUS_POR_TIPO[diagnostico.tipo],
            recordedDate: ahora,
          });
        }
      }

      await lote.enviar(medplum);

      const pares: Par[] = [
        { pregunta: t('formularios.f033.motivoConsulta'), respuesta: motivoConsulta.trim() },
        { pregunta: t('formularios.f033.embarazada'), respuesta: embarazada ? 'Sí' : 'No' },
        { pregunta: t('formularios.f033.enfermedadActual'), respuesta: enfermedadActual.trim() || '—' },
        parAntecedentes(t, t('formularios.f033.seccionAntecedentesPersonales'), 'formularios.f033.antecedentesPersonales', ANTECEDENTES_PERSONALES, antecedentesPersonales, descripcionPersonales),
        parAntecedentes(t, t('formularios.f033.seccionAntecedentesFamiliares'), 'formularios.f033.antecedentesFamiliares', ANTECEDENTES_FAMILIARES, antecedentesFamiliares, descripcionFamiliares),
        {
          pregunta: t('formularios.f033.seccionConstantesVitales'),
          respuesta: `${t('formularios.f033.constantesVitales.temperatura')}: ${temperatura || '—'} | ${t('formularios.f033.constantesVitales.pulso')}: ${pulso || '—'} | ${t('formularios.f033.constantesVitales.frecuenciaRespiratoria')}: ${frecuenciaRespiratoria || '—'} | ${t('formularios.f033.constantesVitales.presionArterial')}: ${presionArterial || '—'}`,
        },
        ...paresEstomatognatico(t, ITEMS_ESTOMATOGNATICO, estomatognatico),
        ...paresIndicadores(t, filasIndicadores),
        {
          pregunta: t('formularios.f033.indicadoresSaludBucal.nivelEnfermedadPeriodontal'),
          respuesta: textoOpcionIndicador(t, 'formularios.f033.indicadoresSaludBucal.nivelEnfermedadPeriodontalOpciones', nivelPeriodontal),
        },
        { pregunta: t('formularios.f033.indicadoresSaludBucal.tipoOclusion'), respuesta: textoOpcionIndicador(t, 'formularios.f033.indicadoresSaludBucal.tipoOclusionOpciones', tipoOclusion) },
        {
          pregunta: t('formularios.f033.indicadoresSaludBucal.nivelFluorosis'),
          respuesta: textoOpcionIndicador(t, 'formularios.f033.indicadoresSaludBucal.nivelFluorosisOpciones', nivelFluorosis),
        },
        { pregunta: t('formularios.f033.seccionIndicesCpoCeo'), respuesta: `CPOD: C${cpod.c}/P${cpod.p}/O${cpod.o}=${cpod.total} — ceo-d: c${ceod.c}/e${ceod.e}/o${ceod.o}=${ceod.total}` },
        { pregunta: t('formularios.f033.examenesComplementarios.pedido'), respuesta: pedidoExamenes.trim() || '—' },
        parInformeExamenes(t, tiposInforme, informeExamenes),
        ...diagnosticos
          .filter((d) => d.valor.texto.trim())
          .map((d, i) => ({
            pregunta: `${t('formularios.f033.seccionDiagnostico')} #${i + 1} (${t(`formularios.f033.diagnostico.${d.tipo}`)})`,
            respuesta: conCodigoCie(d.valor),
          })),
        ...tratamiento.map((sesion, i) => ({
          pregunta: `${t('formularios.f033.seccionTratamiento')} #${i + 1}`,
          respuesta: [sesion.fecha, sesion.diagnosticosComplicaciones, sesion.procedimientos, sesion.prescripciones, sesion.firma].filter(Boolean).join(' | '),
        })),
      ];

      onGuardado(pares, datosEncabezadoMsp, undefined, datosOdontogramaPdf(odontograma));
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{titulo}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <AreaDictado id="f033-motivo-consulta" etiqueta={t('formularios.f033.motivoConsulta')} valor={motivoConsulta} onChange={setMotivoConsulta} filas={2} />

      <div className="flex items-center gap-2">
        <Checkbox id="embarazada" checked={embarazada} onCheckedChange={(v) => setEmbarazada(v === true)} />
        <Label htmlFor="embarazada" className="text-sm font-normal">
          {t('formularios.f033.embarazada')}
        </Label>
      </div>

      <AreaDictado id="f033-enfermedad-actual" etiqueta={t('formularios.f033.enfermedadActual')} valor={enfermedadActual} onChange={setEnfermedadActual} filas={2} />

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f033.seccionAntecedentesPersonales')}</h4>
      <ChecklistGrid claves={ANTECEDENTES_PERSONALES} prefijoI18n="formularios.f033.antecedentesPersonales" prefijoId="antper" valores={antecedentesPersonales} onChange={setAntecedentesPersonales} />
      <AreaDictado id="f033-descripcion-personales" etiqueta={t('formularios.f033.descripcionAntecedentes')} valor={descripcionPersonales} onChange={setDescripcionPersonales} filas={2} />

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f033.seccionAntecedentesFamiliares')}</h4>
      <ChecklistGrid claves={ANTECEDENTES_FAMILIARES} prefijoI18n="formularios.f033.antecedentesFamiliares" prefijoId="antfam" valores={antecedentesFamiliares} onChange={setAntecedentesFamiliares} />
      <AreaDictado id="f033-descripcion-familiares" etiqueta={t('formularios.f033.descripcionAntecedentes')} valor={descripcionFamiliares} onChange={setDescripcionFamiliares} filas={2} />

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f033.seccionConstantesVitales')}</h4>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <div className="flex flex-col gap-1">
          <Label>{t('formularios.f033.constantesVitales.temperatura')}</Label>
          <Input type="number" value={temperatura} onChange={(e) => setTemperatura(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1">
          <Label>{t('formularios.f033.constantesVitales.pulso')}</Label>
          <Input type="number" value={pulso} onChange={(e) => setPulso(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1">
          <Label>{t('formularios.f033.constantesVitales.frecuenciaRespiratoria')}</Label>
          <Input type="number" value={frecuenciaRespiratoria} onChange={(e) => setFrecuenciaRespiratoria(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1">
          <Label>{t('formularios.f033.constantesVitales.presionArterial')}</Label>
          <Input placeholder="120/80" value={presionArterial} onChange={(e) => setPresionArterial(e.target.value)} />
        </div>
      </div>

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f033.seccionExamenEstomatognatico')}</h4>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {ITEMS_ESTOMATOGNATICO.map((item) => (
          <CampoDictado
            key={item}
            id={`f033-estomatognatico-${item}`}
            etiqueta={t(`formularios.f033.examenEstomatognatico.${item}`)}
            placeholder={t('formularios.f033.examenEstomatognatico.descripcionPlaceholder')}
            valor={estomatognatico[item] ?? ''}
            onChange={(v) => setEstomatognatico((prev) => ({ ...prev, [item]: v }))}
          />
        ))}
      </div>

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f033.seccionOdontograma')}</h4>
      <OdontogramaVisual estado={odontograma} onChange={setOdontograma} />

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f033.seccionIndicadoresSaludBucal')}</h4>
      <p className="text-xs text-hce-muted">{t('formularios.f033.indicadoresSaludBucal.ayuda')}</p>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] border-collapse text-sm">
          <thead>
            <tr className="text-left text-xs text-hce-muted">
              <th className="p-1 font-medium">{t('formularios.f033.indicadoresSaludBucal.piezaExaminada')}</th>
              <th className="p-1 font-medium">{t('formularios.f033.indicadoresSaludBucal.placa')} (0-3)</th>
              <th className="p-1 font-medium">{t('formularios.f033.indicadoresSaludBucal.calculo')} (0-3)</th>
              <th className="p-1 font-medium">{t('formularios.f033.indicadoresSaludBucal.gingivitis')} (0-1)</th>
            </tr>
          </thead>
          <tbody>
            {FILAS_INDICADORES.map((piezas, i) => {
              const fila = filasIndicadores[i];
              const actualizar = (parcial: Partial<FilaIndicadores>): void => setFilasIndicadores((prev) => prev.map((f, idx) => (idx === i ? { ...f, ...parcial } : f)));
              const examinada = fila.pieza !== '' && fila.pieza !== PIEZA_RAYA;
              return (
                <tr key={piezas[0]} className="border-t border-border">
                  <td className="p-1">
                    <Select value={fila.pieza || undefined} onValueChange={(v) => actualizar(v === PIEZA_RAYA ? { pieza: v, placa: '', calculo: '', gingivitis: '' } : { pieza: v })}>
                      <SelectTrigger aria-label={`${t('formularios.f033.indicadoresSaludBucal.piezaExaminada')} ${piezas.join('/')}`}>
                        <SelectValue placeholder={piezas.join(' / ')} />
                      </SelectTrigger>
                      <SelectContent>
                        {piezas.map((pz) => (
                          <SelectItem key={pz} value={pz}>
                            {pz}
                          </SelectItem>
                        ))}
                        <SelectItem value={PIEZA_RAYA}>{t('formularios.f033.indicadoresSaludBucal.sinPiezaPresente')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  {(
                    [
                      ['placa', OPCIONES_PLACA],
                      ['calculo', OPCIONES_CALCULO],
                      ['gingivitis', OPCIONES_GINGIVITIS],
                    ] as const
                  ).map(([campo, opciones]) => (
                    <td key={campo} className="p-1">
                      <Select disabled={!examinada} value={fila[campo] || undefined} onValueChange={(v) => actualizar({ [campo]: v })}>
                        <SelectTrigger aria-label={`${t(`formularios.f033.indicadoresSaludBucal.${campo}`)} ${piezas.join('/')}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {opciones.map((op) => (
                            <SelectItem key={op} value={op}>
                              {op}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                  ))}
                </tr>
              );
            })}
            <tr className="border-t-2 border-border font-medium text-hce-ink">
              <td className="p-1">{t('formularios.f033.indicadoresSaludBucal.totales')}</td>
              {(['placa', 'calculo', 'gingivitis'] as const).map((campo) => (
                <td key={campo} className="p-1">
                  {totalesInd[campo] ? totalesInd[campo].promedio : '—'}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      {totalesInd.filasIncompletas > 0 && <p className="text-xs text-hce-clay">{t('formularios.f033.indicadoresSaludBucal.filasIncompletas', { n: totalesInd.filasIncompletas })}</p>}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <CampoSelect
          label={t('formularios.f033.indicadoresSaludBucal.nivelEnfermedadPeriodontal')}
          valor={nivelPeriodontal}
          opciones={OPCIONES_NIVEL}
          prefijoOpcionesI18n="formularios.f033.indicadoresSaludBucal.nivelEnfermedadPeriodontalOpciones"
          onChange={setNivelPeriodontal}
        />
        <CampoSelect label={t('formularios.f033.indicadoresSaludBucal.tipoOclusion')} valor={tipoOclusion} opciones={OPCIONES_OCLUSION} prefijoOpcionesI18n="formularios.f033.indicadoresSaludBucal.tipoOclusionOpciones" onChange={setTipoOclusion} />
        <CampoSelect
          label={t('formularios.f033.indicadoresSaludBucal.nivelFluorosis')}
          valor={nivelFluorosis}
          opciones={OPCIONES_NIVEL}
          prefijoOpcionesI18n="formularios.f033.indicadoresSaludBucal.nivelFluorosisOpciones"
          onChange={setNivelFluorosis}
        />
      </div>

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f033.seccionIndicesCpoCeo')}</h4>
      <div className="grid grid-cols-1 gap-1 rounded-md border border-border p-3 text-sm text-hce-ink sm:grid-cols-2">
        <p>{t('formularios.f033.cpod', { c: cpod.c, p: cpod.p, o: cpod.o, total: cpod.total })}</p>
        <p>{t('formularios.f033.ceod', { c: ceod.c, e: ceod.e, o: ceod.o, total: ceod.total })}</p>
      </div>

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f033.seccionExamenesComplementarios')}</h4>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <AreaDictado id="f033-pedido-examenes" etiqueta={t('formularios.f033.examenesComplementarios.pedido')} valor={pedidoExamenes} onChange={setPedidoExamenes} filas={2} />
        <div className="flex flex-col gap-1">
          <Label>{t('formularios.f033.examenesComplementarios.informe')}</Label>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {TIPOS_INFORME_EXAMENES.map((tipo) => (
              <div key={tipo} className="flex items-center gap-2">
                <Checkbox id={`informe-${tipo}`} checked={tiposInforme[tipo] === true} onCheckedChange={(v) => setTiposInforme((prev) => ({ ...prev, [tipo]: v === true }))} />
                <Label htmlFor={`informe-${tipo}`} className="text-sm font-normal">
                  {t(`formularios.f033.examenesComplementarios.tipos.${tipo}`)}
                </Label>
              </div>
            ))}
          </div>
          <AreaDictado id="f033-informe-examenes" etiqueta="" valor={informeExamenes} onChange={setInformeExamenes} filas={2} />
        </div>
      </div>

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f033.seccionDiagnostico')}</h4>
      <div className="flex flex-col gap-3">
        {diagnosticos.map((d, i) => (
          <div key={i} className="flex flex-col gap-2 rounded-md border border-border p-3">
            <SelectorDiagnosticoCie10
              label={`${t('formularios.f033.diagnostico.entrada')} #${i + 1}`}
              value={d.valor}
              onChange={(valor) => setDiagnosticos((prev) => prev.map((row, idx) => (idx === i ? { ...row, valor } : row)))}
            />
            <CampoSelect
              label={t('formularios.f033.diagnostico.tipo')}
              valor={d.tipo}
              opciones={TIPOS_DIAGNOSTICO}
              prefijoOpcionesI18n="formularios.f033.diagnostico"
              onChange={(v) => setDiagnosticos((prev) => prev.map((row, idx) => (idx === i ? { ...row, tipo: v as DiagnosticoFila['tipo'] } : row)))}
            />
            <Button type="button" variant="ghost" size="sm" className="w-fit" onClick={() => setDiagnosticos((prev) => prev.filter((_, idx) => idx !== i))}>
              <Trash2 className="size-4" /> {t('formularios.f033.diagnostico.quitarEntrada')}
            </Button>
          </div>
        ))}
        <Button type="button" variant="outline" className="w-fit" disabled={diagnosticos.length >= TOPE_DIAGNOSTICOS} onClick={() => setDiagnosticos((prev) => [...prev, { ...DIAGNOSTICO_VACIO }])}>
          {t('formularios.f033.diagnostico.agregarEntrada')}
        </Button>
        {diagnosticos.length >= TOPE_DIAGNOSTICOS && <p className="text-xs text-hce-muted">{t('formularios.f033.diagnostico.topeAlcanzado')}</p>}
      </div>

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f033.seccionTratamiento')}</h4>
      <div className="flex flex-col gap-3">
        {tratamiento.map((sesion, i) => (
          <div key={i} className="flex flex-col gap-2 rounded-md border border-border p-3">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div className="flex flex-col gap-1">
                <Label>{t('formularios.f033.tratamiento.fecha')}</Label>
                <Input type="date" value={sesion.fecha} onChange={(e) => setTratamiento((prev) => prev.map((row, idx) => (idx === i ? { ...row, fecha: e.target.value } : row)))} />
              </div>
              <CampoDictado
                id={`f033-tratamiento-${i}-firma`}
                etiqueta={t('formularios.f033.tratamiento.firma')}
                valor={sesion.firma}
                onChange={(v) => setTratamiento((prev) => prev.map((row, idx) => (idx === i ? { ...row, firma: v } : row)))}
              />
              <div className="col-span-full">
                <AreaDictado
                  id={`f033-tratamiento-${i}-diagcomp`}
                  etiqueta={t('formularios.f033.tratamiento.diagnosticosComplicaciones')}
                  valor={sesion.diagnosticosComplicaciones}
                  onChange={(v) => setTratamiento((prev) => prev.map((row, idx) => (idx === i ? { ...row, diagnosticosComplicaciones: v } : row)))}
                  filas={2}
                />
              </div>
              <div className="col-span-full">
                <AreaDictado
                  id={`f033-tratamiento-${i}-procedimientos`}
                  etiqueta={t('formularios.f033.tratamiento.procedimientos')}
                  valor={sesion.procedimientos}
                  onChange={(v) => setTratamiento((prev) => prev.map((row, idx) => (idx === i ? { ...row, procedimientos: v } : row)))}
                  filas={2}
                />
              </div>
              <div className="col-span-full">
                <AreaDictado
                  id={`f033-tratamiento-${i}-prescripciones`}
                  etiqueta={t('formularios.f033.tratamiento.prescripciones')}
                  valor={sesion.prescripciones}
                  onChange={(v) => setTratamiento((prev) => prev.map((row, idx) => (idx === i ? { ...row, prescripciones: v } : row)))}
                  filas={2}
                />
              </div>
            </div>
            <Button type="button" variant="ghost" size="sm" className="w-fit" onClick={() => setTratamiento((prev) => prev.filter((_, idx) => idx !== i))}>
              <Trash2 className="size-4" /> {t('formularios.f033.tratamiento.quitarSesion')}
            </Button>
          </div>
        ))}
        <Button type="button" variant="outline" className="w-fit" disabled={tratamiento.length >= TOPE_SESIONES} onClick={() => setTratamiento((prev) => [...prev, { ...TRATAMIENTO_VACIO }])}>
          {t('formularios.f033.tratamiento.agregarSesion')}
        </Button>
        {tratamiento.length >= TOPE_SESIONES && <p className="text-xs text-hce-muted">{t('formularios.f033.tratamiento.topeAlcanzado')}</p>}
      </div>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
